import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Container,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Rating,
  MenuItem,
  Snackbar,
  LinearProgress,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Avatar,
} from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  Star as StarIcon,
  RateReview as ReviewIcon,
  Lock as LockIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from "@mui/icons-material";
import { RootState, AppDispatch } from "../store";
import { fetchPublicCaseDetails } from "../store/slices/casesSlice";
import { fetchPublicCaseEvents } from "../store/slices/caseEventsSlice";
import caseFilesService from "../services/caseFilesService";
import StatusBadge from "../components/common/StatusBadge";
import EventDescriptionDisplay from "../components/common/EventDescriptionDisplay";
import { getCaseProgressFromStatus } from "../utils/caseProgress";
import { getEventStatus } from "../utils/eventStatus";
import { useThemeMode } from "../contexts/ThemeModeContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { ONIN_LOGO_LIGHT } from "../components/common/BrandLogo";

const PublicCaseDetails: React.FC = () => {
  const { t } = useTranslation();
  const { caseId, firmId, code } = useParams<{
    caseId: string;
    firmId: string;
    code: string;
  }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { mode, toggleColorMode } = useThemeMode();
  const [mainTab, setMainTab] = useState(0);

  const { currentCase, loading } = useSelector(
    (state: RootState) => state.cases,
  );
  const { logs } = useSelector((state: RootState) => state.caseEvents);
  const [files, setFiles] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Review modal state
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewData, setReviewData] = useState<{
    lawyer_satisfaction: number;
    firm_satisfaction: number;
    recommendation: number;
  }>({
    lawyer_satisfaction: 5,
    firm_satisfaction: 5,
    recommendation: 5,
  });

  const [reviewError, setReviewError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  useEffect(() => {
    if (!caseId || !firmId || !code) {
      setError(t("publicPortal.invalidCaseLink"));
      return;
    }

    const fetchPublicCaseData = async () => {
      try {
        setError(null);

        // Fetch public case details
        const caseResult = await dispatch(
          fetchPublicCaseDetails({
            caseId: parseInt(caseId),
            firmId: parseInt(firmId),
            accessCode: code,
          }),
        );

        if (fetchPublicCaseDetails.rejected.match(caseResult)) {
          setError(t("publicPortal.accessDeniedOrNotFound"));
          return;
        }

        // Fetch public activity logs for this case
        await dispatch(
          fetchPublicCaseEvents({
            caseId: parseInt(caseId),
            accessCode: code,
          }),
        );
      } catch (err: any) {
        const errorMessage = err.message || t("publicPortal.failedToAccess");
        setError(errorMessage);
      }
    };

    fetchPublicCaseData();
  }, [caseId, firmId, code, dispatch, t]);

  useEffect(() => {
    // Parse files from currentCase when it changes
    if (currentCase) {
      const parsedFiles = caseFilesService.parseFilesFromCase(currentCase);
      setFiles(parsedFiles);

      // Auto-open review modal if case is completed and not yet reviewed
      if (
        currentCase.status === "completed" &&
        !currentCase.reviewed &&
        !reviewSubmitted
      ) {
        setTimeout(() => {
          setOpenReviewModal(true);
        }, 1000); // Open after 1 second to allow page to load
      }
    }
  }, [currentCase, reviewSubmitted]);

  const handleSubmitReview = async () => {
    if (!currentCase) return;

    setSubmittingReview(true);
    setReviewError(null);
    const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

    try {
      const response = await fetch(`${BASE_URL}/cases/public/review`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          case_id: currentCase.id,
          lawyer_satisfaction: reviewData.lawyer_satisfaction,
          firm_satisfaction: reviewData.firm_satisfaction,
          recommendation: reviewData.recommendation,
        }),
      });

      const result = await response.json();

      if (result.status) {
        setReviewSubmitted(true);
        setOpenReviewModal(false);
        setSnackbar({
          open: true,
          message: t("publicPortal.reviewSubmittedSuccess"),
          severity: "success",
        });
      } else {
        setReviewError(result.message || t("publicPortal.failedSubmitReview"));
      }
    } catch (error) {
      console.error("Review submission error:", error);
      setReviewError(t("publicPortal.failedSubmitReviewRetry"));
    } finally {
      setSubmittingReview(false);
    }
  };

  const getCurrencySymbol = (currency: string) => {
    const symbols: { [key: string]: string } = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      INR: "₹",
      XOF: "CFA",
    };
    return symbols[currency] || "$";
  };

  // `lawyers_assigned` is a "user_id: fee, user_id: fee" string — this is
  // the one place that parses it, shared by calculateLogAmount (per-entry
  // amount) and the Overview tab (per-lawyer rate next to their name).
  const getLawyerFeesMap = () => {
    const lawyerFees: { [key: string]: number } = {};
    if (
      currentCase?.lawyers_assigned &&
      typeof currentCase.lawyers_assigned === "string"
    ) {
      currentCase.lawyers_assigned.split(",").forEach((entry: string) => {
        const [userId, fee] = entry
          .trim()
          .split(":")
          .map((s: string) => s.trim());
        if (userId && fee) {
          lawyerFees[userId] = parseFloat(fee) || 0;
        }
      });
    }
    return lawyerFees;
  };

  // The hourly rate for a lawyer from `lawyers_list`, or `undefined` when
  // there's no rate on file for them (rather than silently falling back to
  // the $250 guess `calculateLogAmount` uses for billing math — a rate we
  // can't confirm shouldn't be displayed to the client as if it were real).
  const getLawyerRate = (lawyerUserId?: string) => {
    if (!lawyerUserId) return undefined;
    const fee = getLawyerFeesMap()[lawyerUserId];
    return fee > 0 ? fee : undefined;
  };

  const calculateTotalTimeSpent = () => {
    if (!logs) return 0;
    return logs.reduce(
      (sum: number, log: any) => sum + (parseFloat(String(log.time_spent)) || 0),
      0,
    );
  };

  // `currentCase.supervisor` can be the populated user object (public
  // endpoint) or just a name/user_id string depending on which endpoint
  // filled it in — this is the one place that normalizes it to a display
  // name, shared by calculateLogAmount, the Team card, and the estimated
  // breakdown.
  const getSupervisorName = () =>
    currentCase?.supervisor?.full_name ||
    currentCase?.supervisor?.name ||
    currentCase?.supervisor ||
    "";

  // Hours logged under a given display name (lawyer or supervisor) — shared
  // by the Team card (per-person time spent) and the estimated fee
  // breakdown below.
  const getHoursForName = (name: string) => {
    if (!logs || !name) return 0;
    return logs.reduce((sum: number, log: any) => {
      const logName = log?.lawyer?.full_name || log?.lawyer_name || "";
      return logName === name ? sum + (parseFloat(String(log.time_spent)) || 0) : sum;
    }, 0);
  };

  const calculateLogAmount = (log: any) => {
    if (!currentCase || currentCase.billing_type !== "hourly") {
      return 0;
    }

    const lawyerFees = getLawyerFeesMap();

    // Get lawyer name
    let lawyerName = "";
    if (log?.lawyer?.full_name) {
      lawyerName = log.lawyer.full_name;
    } else if (log?.lawyer_name) {
      lawyerName = log.lawyer_name;
    }

    const supervisorName = getSupervisorName();

    let hourlyRate = 0;
    if (supervisorName === lawyerName && currentCase.supervisor_fee > 0) {
      hourlyRate = parseFloat(String(currentCase.supervisor_fee)) || 0;
    } else {
      // Find the lawyer's user_id from lawyers_list to get their fee
      const lawyerInfo = currentCase.lawyers_list?.find(
        (lawyer: any) => lawyer.full_name === lawyerName,
      );
      hourlyRate = lawyerInfo ? lawyerFees[lawyerInfo.user_id] || 250 : 250;
    }

    const timeSpent = parseFloat(String(log.time_spent)) || 0;
    return timeSpent * hourlyRate;
  };

  const calculateTotalCharges = () => {
    if (!currentCase || !logs) return 0;
    return currentCase.total_charges;
  };

  // Actual generated invoices for this case, each with the itemized
  // breakdown (supervisor/lawyer, hours, rate) the admin's "Generate Bill"
  // action produced — `total_charges` alone only shows the final number,
  // not how it was arrived at. `items` comes back as a JSON string (the
  // backend stores it via `json_encode()`, no cast on the Bill model), so
  // it needs parsing here rather than in the render.
  const getBills = () => {
    if (!currentCase?.bills) return [];
    return currentCase.bills.map((bill) => {
      let items: Array<{ description: string; quantity: number; unit_price: number; total: number }> = [];
      try {
        items = typeof bill.items === "string" ? JSON.parse(bill.items) : bill.items || [];
      } catch {
        items = [];
      }
      return { ...bill, items };
    });
  };

  // Same idea as a generated bill's line items, but computed live from the
  // case's own data (logged hours, supervisor/lawyer rates) for when no
  // invoice has been created yet — so the client always sees *something*
  // explaining the total, not just a blank "no invoice yet" card. Mirrors
  // the admin's own bill-generation grouping (CaseDetails.tsx), except it
  // never fabricates a rate for a lawyer with none on file (no $250
  // fallback) — an unconfirmed number shouldn't be shown to the client.
  const calculateEstimatedBreakdown = () => {
    if (!currentCase) return [];

    if (currentCase.billing_type === "fixed") {
      return [{ description: t("cases.fixedFee"), total: currentCase.total_charges || 0 }];
    }

    const supervisorName = getSupervisorName();
    const items: { description: string; total: number }[] = [];

    if (supervisorName && currentCase.supervisor_fee > 0) {
      const hours = getHoursForName(supervisorName);
      if (hours > 0) {
        const rate = parseFloat(String(currentCase.supervisor_fee)) || 0;
        items.push({
          description: t("cases.supervisorChargeDescription", { name: supervisorName, hours }),
          total: hours * rate,
        });
      }
    }

    (currentCase.lawyers_list || []).forEach((lawyer: any) => {
      if (lawyer.full_name === supervisorName) return; // already billed above
      const rate = getLawyerRate(lawyer.user_id);
      const hours = getHoursForName(lawyer.full_name);
      if (!rate || hours <= 0) return;
      items.push({
        description: t("cases.lawyerChargeDescription", {
          name: lawyer.full_name,
          hours,
          currency: getCurrencySymbol(currentCase.currency),
          rate,
        }),
        total: hours * rate,
      });
    });

    return items;
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      pending: "#f97316",
      in_progress: "#3B82F6",
      completed: "#22c55e",
      archived: "#64748b",
      closed: "#6B7280",
      cancelled: "#EF4444",
      on_hold: "#F59E0B",
      processing: "#3b82f6",
    };
    return colors[status] || colors.pending;
  };

  const handleViewFile = (file: any) => {
    const fileUrl = caseFilesService.getPublicFileUrl(file);
    window.open(fileUrl, "_blank");
  };

  const handleDownloadFile = (file: any) => {
    const fileUrl = caseFilesService.getPublicFileUrl(file);
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = file.original_filename || file.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Container component="main" maxWidth="md">
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {t("publicPortal.loadingCaseDetails")}
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !currentCase) {
    return (
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <Card sx={{ width: "100%", maxWidth: 400 }}>
            <CardContent sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h5" color="error" gutterBottom>
                {t("errors.accessDenied")}
              </Typography>
              <Alert severity="error" sx={{ mb: 3 }}>
                {error || t("publicPortal.unableToAccess")}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("publicPortal.contactFirm")}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => window.history.back()}
                sx={{ mr: 1 }}
              >
                {t("publicPortal.goBack")}
              </Button>
              <Button variant="contained" onClick={() => navigate("/")}>
                {t("publicPortal.home")}
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  const customer = (currentCase as any).customer;
  const firm = (currentCase as any).firm;
  // Same stage-based calculation the cases list/kanban use, so the
  // percentage shown here always matches what's shown there for this case.
  const caseProgress = getCaseProgressFromStatus(currentCase.status);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Top bar */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          px: { xs: 2, sm: 4 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            component="img"
            src={firm?.logo_url || ONIN_LOGO_LIGHT}
            alt={firm?.name || t("publicPortal.firmLogoAlt")}
            onError={(e) => {
              (e.target as HTMLImageElement).src = ONIN_LOGO_LIGHT;
            }}
            sx={{
              height: 36,
              width: 36,
              borderRadius: 1.5,
              objectFit: "contain",
              bgcolor: "grey.100",
            }}
          />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {firm?.name || t("publicPortal.clientPortal")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {customer?.company || t("publicPortal.secureCaseAccess")}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Chip
            icon={<LockIcon fontSize="small" />}
            label={t("publicPortal.secureLink")}
            size="small"
            color="success"
            variant="outlined"
          />
          <LanguageSwitcher />
          <Tooltip title={mode === "dark" ? t("publicPortal.switchToLightMode") : t("publicPortal.switchToDarkMode")}>
            <IconButton onClick={toggleColorMode} size="small">
              {mode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Case header card */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="overline" sx={{ color: "primary.main", fontWeight: 700 }}>
              {t("publicPortal.caseNumberLabel", { id: currentCase.id })}
            </Typography>
            <Typography variant="h4" sx={{ mb: 1.5 }}>
              {currentCase.subject}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 3 }}>
              <StatusBadge status={currentCase.status} />
              {currentCase.type && <Chip label={currentCase.type} size="small" variant="outlined" />}
              <Chip
                label={currentCase.billing_type === "fixed" ? t("publicPortal.fixedBilling") : t("publicPortal.hourlyBilling")}
                size="small"
                variant="outlined"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
                  {t("dashboard.progress")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {caseProgress}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={caseProgress} sx={{ height: 8, borderRadius: 4 }} />
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              <Box sx={{ flex: "1 1 160px" }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
                  {t("publicPortal.responsible")}
                </Typography>
                <Typography variant="body1">
                  {currentCase.supervisor?.full_name ||
                    currentCase.lawyers_list?.[0]?.full_name ||
                    t("publicPortal.notAssigned")}
                </Typography>
              </Box>
              <Box sx={{ flex: "1 1 160px" }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
                  {t("publicPortal.opened")}
                </Typography>
                <Typography variant="body1">
                  {currentCase.start_date
                    ? new Date(currentCase.start_date).toLocaleDateString()
                    : t("publicPortal.notSet")}
                </Typography>
              </Box>
              <Box sx={{ flex: "1 1 160px" }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
                  {t("publicPortal.estimated")}
                </Typography>
                <Typography variant="body1">
                  {currentCase.end_date
                    ? new Date(currentCase.end_date).toLocaleDateString()
                    : t("publicPortal.notSet")}
                </Typography>
              </Box>
            </Box>

            {currentCase.status === "completed" && !currentCase.reviewed && (
              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  startIcon={<ReviewIcon />}
                  onClick={() => setOpenReviewModal(true)}
                  sx={{ borderRadius: 2 }}
                >
                  {t("publicPortal.leaveReview")}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          sx={{ mb: 2, minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none" } }}
        >
          <Tab label={t("publicPortal.tabOverview")} />
          <Tab label={t("publicPortal.tabTimeline")} />
          <Tab label={t("publicPortal.tabDocuments")} />
          <Tab label={t("publicPortal.tabBilling")} />
        </Tabs>

        {/* Overview */}
        {mainTab === 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Card sx={{ flex: "1 1 260px" }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("publicPortal.team")}
                </Typography>
                {(() => {
                  // Supervisor first (hourly billing only — that's the only
                  // case a rate/hours-logged figure means anything), then
                  // every assigned lawyer. Previously this card only ever
                  // listed lawyers_list, so the supervisor never showed up
                  // here at all despite being billed the same way.
                  const supervisorName = getSupervisorName();
                  const teamMembers = [
                    ...(currentCase.billing_type === "hourly" && currentCase.supervisor && supervisorName
                      ? [
                          {
                            key: "supervisor",
                            full_name: supervisorName,
                            roleLabel: t("publicPortal.responsible"),
                            rate: currentCase.supervisor_fee > 0 ? currentCase.supervisor_fee : undefined,
                            hours: getHoursForName(supervisorName),
                          },
                        ]
                      : []),
                    ...(currentCase.lawyers_list || []).map((lawyer: any) => ({
                      key: lawyer.user_id || lawyer.full_name,
                      full_name: lawyer.full_name,
                      roleLabel: undefined as string | undefined,
                      rate: getLawyerRate(lawyer.user_id),
                      hours: getHoursForName(lawyer.full_name),
                    })),
                  ];

                  if (teamMembers.length === 0) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        {t("publicPortal.noLawyersAssigned")}
                      </Typography>
                    );
                  }

                  return (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {teamMembers.map((member) => (
                        <Box key={member.key} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar sx={{ width: 32, height: 32, fontSize: "0.75rem", bgcolor: "primary.main" }}>
                            {(member.full_name || "?")
                              .split(" ")
                              .map((n: string) => n[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </Avatar>
                          <Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Typography variant="body2">{member.full_name}</Typography>
                              {member.roleLabel && (
                                <Chip label={member.roleLabel} size="small" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
                              )}
                            </Box>
                            {currentCase.billing_type === "hourly" && member.rate !== undefined && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t("publicPortal.hourlyRate", {
                                  symbol: getCurrencySymbol(currentCase.currency),
                                  rate: member.rate,
                                })}
                              </Typography>
                            )}
                            {currentCase.billing_type === "hourly" && member.hours > 0 && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t("publicPortal.hoursLogged", {
                                  hours: member.hours,
                                  unit: t("publicPortal.hoursSuffix"),
                                })}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>

            <Card sx={{ flex: "1 1 260px" }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {t("publicPortal.tabBilling")}
                </Typography>
                <Typography variant="body2">
                  {currentCase.billing_type === "fixed"
                    ? t("publicPortal.fixedFeeLabel", {
                        symbol: getCurrencySymbol(currentCase.currency),
                        amount: currentCase.total_charges,
                      })
                    : t("publicPortal.hourlyBillingLabel", {
                        symbol: getCurrencySymbol(currentCase.currency),
                        fee: currentCase.fee || 0,
                      })}
                </Typography>
                {currentCase.billing_type === "hourly" && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("publicPortal.totalTimeSpent")}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {calculateTotalTimeSpent()} {t("publicPortal.hoursSuffix")}
                    </Typography>
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t("publicPortal.detailedActivityAvailable")}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Timeline */}
        {mainTab === 1 && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              {!logs || logs.length === 0 ? (
                <Typography color="text.secondary">{t("publicPortal.noEvents")}</Typography>
              ) : (
                <Box>
                  {logs.map((log: any, index: number) => {
                    const amount = calculateLogAmount(log);
                    const isLast = index === logs.length - 1;
                    const activityStatus = getEventStatus(log.status, currentCase.status);
                    return (
                      <Box key={log.id} sx={{ display: "flex", gap: 2 }}>
                        <Box
                          sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 20 }}
                        >
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: getStatusColor(activityStatus),
                              mt: 2,
                              flexShrink: 0,
                            }}
                          />
                          {!isLast && (
                            <Box sx={{ flex: 1, width: "2px", bgcolor: "divider", my: 0.5 }} />
                          )}
                        </Box>
                        <Card variant="outlined" sx={{ flex: 1, mb: isLast ? 0 : 2, p: 2, borderRadius: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", flex: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {log.title || log.description || log.activity_description}
                              </Typography>
                              <StatusBadge status={activityStatus} size="small" />
                            </Box>
                            {currentCase.billing_type === "hourly" && amount > 0 && (
                              <Typography variant="body2" color="primary" sx={{ fontWeight: 700, flexShrink: 0 }}>
                                {getCurrencySymbol(currentCase.currency)}
                                {amount.toFixed(2)}
                              </Typography>
                            )}
                          </Box>
                          <EventDescriptionDisplay content={log.description || log.activity_description} sx={{ mt: 1, mb: 1 }} />
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              {t("publicPortal.byLawyer", { name: log?.lawyer?.full_name })}
                            </Typography>
                            {log?.lawyer?.position && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem", opacity: 0.75 }}>
                                {typeof log.lawyer.position === "string"
                                  ? log.lawyer.position
                                  : log.lawyer.position?.name}
                              </Typography>
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {(() => {
                              const date = new Date(log.created_at);
                              const month = String(date.getMonth() + 1).padStart(2, "0");
                              const day = String(date.getDate()).padStart(2, "0");
                              const year = date.getFullYear();
                              const hours = String(date.getHours()).padStart(2, "0");
                              const minutes = String(date.getMinutes()).padStart(2, "0");
                              return `${month}-${day}-${year} • ${hours}:${minutes}`;
                            })()} • {log.time_spent} {t("publicPortal.hoursSuffix")}
                          </Typography>
                        </Card>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Documents */}
        {mainTab === 2 && (
          <Card>
            <CardContent sx={{ p: 3 }}>
              {!files || files.length === 0 ? (
                <Typography color="text.secondary">{t("publicPortal.noDocumentsYet")}</Typography>
              ) : (
                <Grid container spacing={2}>
                  {files.map((file) => (
                    <Grid item key={file.id} xs={12} sm={6} md={4}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: "pointer",
                          transition: "all 0.2s",
                          "&:hover": {
                            boxShadow: 2,
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        <CardContent sx={{ pb: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                bgcolor: "primary.light",
                                color: "primary.contrastText",
                              }}
                            >
                              <FileIcon />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: "medium",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {file.original_filename || file.filename}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {caseFilesService.formatFileSize(file.file_size)}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {new Date(file.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                        <CardActions sx={{ pt: 0, px: 2, pb: 1 }}>
                          <Button size="small" startIcon={<ViewIcon />} onClick={() => handleViewFile(file)}>
                            {t("common.view")}
                          </Button>
                          <Button
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadFile(file)}
                          >
                            {t("common.download")}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        )}

        {/* Billing */}
        {mainTab === 3 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6">{t("publicPortal.totalFee")}</Typography>
                  <Typography variant="h4" color="primary">
                    {getCurrencySymbol(currentCase.currency)}
                    {calculateTotalCharges()}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  {t("publicPortal.billingTypeLabel")}{" "}
                  <Box component="strong" sx={{ textTransform: "capitalize" }}>
                    {currentCase.billing_type}
                  </Box>
                </Typography>
              </CardContent>
            </Card>

            {/* Fee breakdown — each generated invoice's line items (who,
                how many hours, at what rate), not just the final total
                above. When no invoice has been generated yet, fall back to
                the same breakdown computed live from the case's own data,
                so the client always sees what the total is made up of
                rather than a blank card. */}
            {getBills().length === 0 ? (
              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t("publicPortal.feeBreakdown")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                    {t("publicPortal.noInvoicesYet")}
                  </Typography>
                  {calculateEstimatedBreakdown().length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {t("publicPortal.detailedActivityAvailable")}
                    </Typography>
                  ) : (
                    calculateEstimatedBreakdown().map((item, index, arr) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          py: 1,
                          borderBottom: index < arr.length - 1 ? "1px solid" : "none",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2">{item.description}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                          {getCurrencySymbol(currentCase.currency)}
                          {item.total}
                        </Typography>
                      </Box>
                    ))
                  )}
                </CardContent>
              </Card>
            ) : (
              getBills().map((bill) => (
                <Card key={bill.id}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {t("publicPortal.invoiceNumber", { number: bill.bill_number })}
                      </Typography>
                      <Chip label={bill.status} size="small" variant="outlined" sx={{ textTransform: "capitalize" }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {t("publicPortal.feeBreakdown")}
                    </Typography>
                    {(bill.items as any[]).map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          py: 1,
                          borderBottom: index < (bill.items as any[]).length - 1 ? "1px solid" : "none",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2">{item.description}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
                          {getCurrencySymbol(bill.currency)}
                          {item.total}
                        </Typography>
                      </Box>
                    ))}
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t("publicPortal.totalFee")}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {getCurrencySymbol(bill.currency)}
                        {bill.total}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {t("publicPortal.footerRights", { year: new Date().getFullYear() })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("publicPortal.securePortalNotice")}
          </Typography>
        </Box>
      </Container>

      {/* Review Modal */}
      <Dialog
        open={openReviewModal}
        onClose={() => setOpenReviewModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <StarIcon sx={{ color: "primary.main", fontSize: 28 }} />
            <Typography variant="h5">{t("publicPortal.rateExperience")}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("publicPortal.thankYouFeedback")}
          </Typography>

          {reviewError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {reviewError}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Lawyer Satisfaction */}
            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                {t("publicPortal.lawyerSatisfactionQuestion")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Rating
                  value={reviewData.lawyer_satisfaction}
                  onChange={(_, newValue) => {
                    setReviewData((prev) => ({
                      ...prev,
                      lawyer_satisfaction: newValue || 0,
                    }));
                  }}
                  size="large"
                  icon={<StarIcon fontSize="inherit" />}
                  emptyIcon={<StarIcon fontSize="inherit" />}
                />
                <Typography variant="body2" color="text.secondary">
                  {reviewData.lawyer_satisfaction} / 5
                </Typography>
              </Box>
            </Box>

            {/* Firm Satisfaction */}
            <Box>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{ fontWeight: 600 }}
              >
                {t("publicPortal.firmSatisfactionQuestion")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Rating
                  value={reviewData.firm_satisfaction}
                  onChange={(_, newValue) => {
                    setReviewData((prev) => ({
                      ...prev,
                      firm_satisfaction: newValue || 0,
                    }));
                  }}
                  size="large"
                  icon={<StarIcon fontSize="inherit" />}
                  emptyIcon={<StarIcon fontSize="inherit" />}
                />
                <Typography variant="body2" color="text.secondary">
                  {reviewData.firm_satisfaction} / 5
                </Typography>
              </Box>
            </Box>

            {/* Recommendation */}
            <TextField
              select
              label={t("publicPortal.recommendQuestion")}
              value={reviewData.recommendation}
              onChange={(e) =>
                setReviewData((prev) => ({
                  ...prev,
                  recommendation: parseInt(e.target.value),
                }))
              }
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            >
              <MenuItem value={1}>{t("publicPortal.recommendNo")}</MenuItem>
              <MenuItem value={2}>{t("publicPortal.recommendProbablyNot")}</MenuItem>
              <MenuItem value={3}>{t("publicPortal.recommendMaybe")}</MenuItem>
              <MenuItem value={4}>{t("publicPortal.recommendProbablyYes")}</MenuItem>
              <MenuItem value={5}>{t("publicPortal.recommendYesDefinitely")}</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setOpenReviewModal(false)}
            disabled={submittingReview}
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSubmitReview}
            variant="contained"
            disabled={submittingReview}
            startIcon={
              submittingReview ? <CircularProgress size={16} /> : <StarIcon />
            }
            sx={{ borderRadius: 2 }}
          >
            {submittingReview ? t("publicPortal.submitting") : t("publicPortal.submitReview")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PublicCaseDetails;
